/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import moment from "moment";
import VideoEntry from "./library-components/video-entry";
import { fetchSignalIndex, deleteVideo, bulkDelete } from "../../entities/helpers/thankview-helper";
import { useRecorder } from "./hooks/recorder-context";
import useUserAuthentication from "./hooks/useUserAuthentication";
import { SearchInput, AdvancedCombobox, Icon, Button, Loading } from "@evertrue/et-components";
import EmptyStateMessage from "components/lists/empty-state-message-tv";
import classNames from "classnames";
import DateRangeFilter from "./library-components/video-library-date-picker";

const PAGE_LIMIT = 10;
function findConfirmButton() {
  const modalHeaders = document.querySelectorAll(".et--modal-header");
  for (const header of modalHeaders) {
    const h3 = header.querySelector("h3");
    if (h3 && h3.textContent.trim() === "Video Library") {
      return header.querySelector(".et--modal-header--actions button.et--button:not(.et--button-simple)");
    }
  }
  return null;
}

function disableConfirmButton() {
  const button = findConfirmButton();
  if (button) {
    button.disabled = true;
    button.classList.add("disabled");
    button.textContent = "Cannot Confirm";
  } else {
    console.log("Confirm button not found in the Video Library modal");
  }
}

function enableConfirmButton() {
  const button = findConfirmButton();
  if (button) {
    button.disabled = false;
    // button.classList.remove("is-disabled");
    button.classList.remove("disabled");
    button.textContent = "Confirm";
  } else {
    console.log("Confirm button not found in the Video Library modal");
  }
}
const filterTypes = [
  { label: "Date Created", value: "created_at", defaultOrder: "desc" },
  { label: "Date Modified", value: "updated_at", defaultOrder: "desc" },
  { label: "Title", value: "name", defaultOrder: "asc" },
  { label: "Duration", value: "video_length", defaultOrder: "asc" },
];

function ThankviewLibrary() {
  const { getBrowserTimezone, handleLibraryVideo, updateState, state } = useRecorder();
  const { clientId } = useUserAuthentication();
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [userTimezone, setUserTimezone] = useState("UTC");
  // const [startsOn, setStartsOn] = useState(() => moment().tz(userTimezone).startOf("day"));
  const [searchText, setSearchText] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(filterTypes[0]);
  const [isAscOrder, setIsAscOrder] = useState(filterTypes[0].defaultOrder === "asc");
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState([]);
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [count, setCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const handleDateRangeChange = (start, end) => {
    console.log(start, end);
    setStartDate(start);
    setEndDate(end);
  };

  const observerTarget = useRef(null);

  const isAllSelected = useMemo(
    () => selectedVideos.length === listings.length && listings.length > 0,
    [selectedVideos, listings]
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        let client_id = clientId && clientId.data.client_id;
        let client_secret = clientId && clientId.data.client_secret;
        const data = {
          sort: "created_at",
          order: "desc",
          page: 1,
          limit: 5,
          filters: JSON.stringify({ is_signal: true, user_id: clientId.data.id }),
        };
        const results = await fetchSignalIndex(data, client_id, client_secret);
        const uniquePersonalVideos = [];
        const uniqueLibraryVideos = [];
        const seenPersonalIds = new Set();
        const seenLibraryIds = new Set();

        results.data.personalVideos.forEach((video) => {
          if (!seenPersonalIds.has(video.id)) {
            seenPersonalIds.add(video.id);
            uniquePersonalVideos.push(video);
          }
        });

        results.data.libraryVideos.forEach((video) => {
          if (!seenLibraryIds.has(video.id)) {
            seenLibraryIds.add(video.id);
            uniqueLibraryVideos.push(video);
          }
        });

        setListings((prev) => (pageNum === 1 ? uniquePersonalVideos : [...prev, ...uniquePersonalVideos]));
        setLibraryVideos((prev) => (pageNum === 1 ? uniqueLibraryVideos : [...prev, ...uniqueLibraryVideos]));
        const currentCount = (pageNum - 1) * PAGE_LIMIT + uniquePersonalVideos.length;
        setCount(currentCount);
        setTotalCount(results.data.count);
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };

    fetchInitialData();
  }, [clientId]);
  useEffect(() => {
    const timezone = getBrowserTimezone();
    if (timezone !== userTimezone) {
      setUserTimezone(timezone);
    }
  }, [getBrowserTimezone, userTimezone]);

  const fetchData = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const { client_id, client_secret } = clientId.data;
        const filters = {
          is_signal: true,
          name: searchText,
          user_id: clientId.data.id,
          from_date: startDate ? moment(startDate).tz(userTimezone).startOf("day").format() : "",
          to_date: endDate ? moment(endDate).tz(userTimezone).endOf("day").format() : "",
        };

        const data = {
          sort: selectedFilter.value,
          order: isAscOrder ? "asc" : "desc",
          page,
          limit: PAGE_LIMIT,
          filters: JSON.stringify(filters),
        };

        const results = await fetchSignalIndex(data, client_id, client_secret);
        processResults(results, page);
      } catch (err) {
        console.error("Error fetching data in video library:", err);
      } finally {
        setLoading(false);
      }
    },
    [clientId, isAscOrder, searchText, selectedFilter.value, startDate, endDate, userTimezone]
  );

  useEffect(() => {
    fetchData(pageNum);
  }, [isAscOrder, searchText, selectedFilter.value, startDate, endDate, pageNum]);

  const processResults = useCallback((results, page) => {
    const { personalVideos, libraryVideos, count } = results.data;
    const uniquePersonalVideos = removeDuplicates(personalVideos);
    const uniqueLibraryVideos = removeDuplicates(libraryVideos);

    setListings((prev) => (page === 1 ? uniquePersonalVideos : [...prev, ...uniquePersonalVideos]));
    setLibraryVideos((prev) => (page === 1 ? uniqueLibraryVideos : [...prev, ...uniqueLibraryVideos]));
    setCount((page - 1) * PAGE_LIMIT + uniquePersonalVideos.length);
    setTotalCount(count);
  }, []);

  const removeDuplicates = useCallback((videos) => {
    const seenIds = new Set();
    return videos.filter((video) => {
      if (!seenIds.has(video.id)) {
        seenIds.add(video.id);
        return true;
      }
      return false;
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && totalCount > 0 && count < totalCount) {
          setPageNum((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    const target = observerTarget.current;
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [loading, totalCount, count]);

  useEffect(() => {
    setListings([]);
    setLibraryVideos([]);
    setCount(0);
    setTotalCount(0);
    setPageNum(1);
    setSelectedVideos([]);
  }, [searchText, selectedFilter.value, isAscOrder, startDate, endDate]);

  useEffect(() => {
    if (selectedVideos.length === 1) {
      const selectedVideo = listings.find((video) => video.id === selectedVideos[0]);
      if (selectedVideo && selectedVideo.library_video_id) {
        const libraryVideo = libraryVideos.find((libraryVideo) => libraryVideo.id === selectedVideo.library_video_id);
        handleLibraryVideo({
          data: {
            personalVideo: selectedVideo,
            libraryVideo: libraryVideo || "",
          },
        });
        updateState({
          isSelectedLibraryVideosMoreThanOne: false,
          isSelectedLibraryVideosAreNone: false,
        });
      }
    } else if (selectedVideos.length > 1) {
      updateState({
        isSelectedLibraryVideosMoreThanOne: true,
        isSelectedLibraryVideosAreNone: false,
      });
    } else if (selectedVideos.length === 0) {
      updateState({
        isSelectedLibraryVideosMoreThanOne: false,
        isSelectedLibraryVideosAreNone: true,
      });
    }
  }, [handleLibraryVideo, libraryVideos, listings, selectedVideos, state, updateState]);

  useEffect(() => {
    if (state.isSelectedLibraryVideosMoreThanOne || state.isSelectedLibraryVideosAreNone) {
      disableConfirmButton();
    } else {
      enableConfirmButton();
    }
  }, [state]);

  const toggleVideoSelection = useCallback(
    (videoId) => {
      setSelectedVideos((prev) => (prev.includes(videoId) ? prev.filter((id) => id !== videoId) : [...prev, videoId]));
    },
    [selectedVideos.length, updateState]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedVideos(isAllSelected ? [] : listings.map((video) => video.id));
  }, [isAllSelected, listings]);

  const deleteVideoEntry = useCallback(
    async (videoId) => {
      try {
        const client_id = clientId.data.client_id;
        const client_secret = clientId.data.client_secret;
        await deleteVideo(videoId, client_id, client_secret);
        fetchData();
      } catch (err) {
        console.error("Error deleting video:", err);
      }
    },
    [clientId, fetchData]
  );

  const handleBulkDelete = useCallback(async () => {
    try {
      const { client_id, client_secret } = clientId.data;
      await bulkDelete({ personalVideoIds: selectedVideos, selectAll: false }, client_id, client_secret);
      fetchData();
    } catch (err) {
      console.error("Error deleting videos:", err);
    }
  }, [clientId, fetchData, selectedVideos]);

  const renderResults = () => {
    if (count === 0) {
      return (
        <>
          <EmptyStateMessage icon="logo" iconKit="tv" text="No Signal Videos Found" />
          {loading && <Loading />}
        </>
      );
    }

    return (
      <>
        <SelectionHeader
          isAllSelected={isAllSelected}
          selectedCount={selectedVideos.length}
          onSelectAll={handleSelectAll}
          onBulkDelete={handleBulkDelete}
        />
        <VideoList
          listings={listings}
          libraryVideos={libraryVideos}
          selectedVideos={selectedVideos}
          onToggleSelection={toggleVideoSelection}
          onDeleteVideo={deleteVideoEntry}
        />
        {loading && <Loading />}
        <div ref={observerTarget} style={{ height: "1px" }}></div>
      </>
    );
  };

  return (
    <div id="video-library-parent-container" className="video-library-parent-container">
      <ControlTools
        searchText={searchText}
        setSearchText={setSearchText}
        count={count}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        isAscOrder={isAscOrder}
        setIsAscOrder={setIsAscOrder}
        onDateRangeChange={handleDateRangeChange}
      />
      <div>{renderResults()}</div>
    </div>
  );
}

const SelectionHeader = ({ isAllSelected, selectedCount, onSelectAll, onBulkDelete }) => (
  <div style={{ display: "flex" }}>
    <input
      style={{ margin: "12px" }}
      type="checkbox"
      value="select-all"
      checked={isAllSelected}
      onChange={onSelectAll}
    />
    <p style={{ margin: "10px" }}>
      {selectedCount} video{selectedCount !== 1 ? "s" : ""} selected
    </p>
    {selectedCount > 0 && (
      <Button label="Delete Selected Videos" type="simple" onClick={onBulkDelete}>
        <Icon icon="trash" className="proposal-detail--edit-icon" />
      </Button>
    )}
  </div>
);

const VideoList = ({ listings, libraryVideos, selectedVideos, onToggleSelection, onDeleteVideo }) => (
  <>
    {listings.map((video) => {
      const libraryVideo = libraryVideos.find((x) => x.id === video.library_video_id);
      return (
        <li key={video.id} className="video-library--entry">
          <VideoEntry
            video={video}
            libraryVideo={libraryVideo}
            deleteVideoEntry={onDeleteVideo}
            isSelected={selectedVideos.includes(video.id)}
            onToggleSelection={() => onToggleSelection(video.id)}
          />
        </li>
      );
    })}
  </>
);

const ControlTools = ({
  searchText,
  setSearchText,
  count,
  selectedFilter,
  setSelectedFilter,
  isAscOrder,
  setIsAscOrder,
  onDateRangeChange,
}) => (
  <div className="video-library--control-tools">
    <div className="video-library--top-row">
      <SearchInput
        value={searchText}
        onChange={setSearchText}
        width={350}
        placeholder="Search for a Video"
        debounceTimeout={300}
      />
      <div className="video-count">{count} Videos</div>
    </div>
    <div className="video-library--bottom-row">
      <p>Sort By</p>
      <AdvancedCombobox onChange={setSelectedFilter} value={selectedFilter} options={filterTypes} />
      <Button
        className={classNames("order-button", { "is-asc": isAscOrder })}
        onClick={() => setIsAscOrder(!isAscOrder)}
        label="order of videos"
      >
        <Icon icon="arrow-down" iconKit="gt" />
      </Button>
      <span style={{ marginTop: "10px" }}>
        <DateRangeFilter onDateRangeChange={onDateRangeChange} />
      </span>
    </div>
  </div>
);
SelectionHeader.propTypes = {
  isAllSelected: PropTypes.bool.isRequired,
  selectedCount: PropTypes.number.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onBulkDelete: PropTypes.func.isRequired,
};

VideoList.propTypes = {
  listings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ).isRequired,
  libraryVideos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedVideos: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleSelection: PropTypes.func.isRequired,
  onDeleteVideo: PropTypes.func.isRequired,
};

ControlTools.propTypes = {
  searchText: PropTypes.string.isRequired,
  setSearchText: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  selectedFilter: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    defaultOrder: PropTypes.string.isRequired,
  }).isRequired,
  setSelectedFilter: PropTypes.func.isRequired,
  isAscOrder: PropTypes.bool.isRequired,
  setIsAscOrder: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
};

const videoShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

VideoEntry.propTypes = {
  video: videoShape.isRequired,
  libraryVideo: videoShape,
  deleteVideoEntry: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggleSelection: PropTypes.func.isRequired,
};

export default React.memo(ThankviewLibrary);
